import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { toParisTime, fmtDuration } from "../utils/date";
import Spinner from "./ui/Spinner";

/* ══════════════════════════════════════════════════════════════════
   Modale de réassociation manuelle formulaire ↔ essai (P0-Q, revu le
   21/07/2026 pour l'algorithme final)

   Charge lazily les essais détectés du site (ESSAIS_DETECTED_DIR, cache
   persistant de essai_detection.py) et permet d'en choisir un pour forcer
   l'association du formulaire (PATCH manual=true). Remplace le choix d'un
   événement Syrinix brut : un événement peut contenir plusieurs essais
   distincts sous le nouvel algorithme, choisir l'événement entier ne
   permettait plus de cibler précisément lequel — et la réassociation était
   silencieusement sans effet sur un essai déjà associé (t_debut/t_fin
   existants jamais mis à jour), corrigé en renvoyant les bornes précises de
   l'essai choisi (start_ts/end_ts) au PATCH, cf. docs/REFONTE_ASSOCIATION.md §24.

   Props :
     formId         — identifiant du formulaire à réassocier
     siteId         — site dont on charge les essais détectés
     currentEventId — essai actuellement associé (présélection, optionnel —
                      matched_event de l'enregistrement courant)
     onClose        — fermeture sans sauvegarde
     onSaved        — appelé après un PATCH réussi
   ══════════════════════════════════════════════════════════════════ */
export default function ReassocModal({ formId, siteId, currentEventId, onClose, onSaved }) {
  const [essais, setEssais]         = useState(null);
  const [loadingEvs, setLoadingEvs] = useState(true);
  const [saving, setSaving]         = useState(false);
  const [selectedIdx, setSelectedIdx] = useState("");
  const [error, setError]           = useState(null);

  useEffect(() => {
    apiFetch(`/api/essais/${siteId}/candidates`)
      .then(list => {
        setEssais(list);
        setLoadingEvs(false);
        if (currentEventId != null) {
          const idx = list.findIndex(e => String(e.event_id) === String(currentEventId));
          if (idx >= 0) setSelectedIdx(String(idx));
        }
      })
      .catch(e => { setError(e.message); setLoadingEvs(false); });
  }, [siteId]);

  const save = async () => {
    if (selectedIdx === "" || !essais) return;
    const essai = essais[Number(selectedIdx)];
    if (!essai) return;
    setSaving(true);
    try {
      await apiFetch(`/api/match/association/${siteId}/${formId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: String(essai.event_id),
          start_ts: essai.start_ts,
          end_ts: essai.end_ts,
        }),
      });
      onSaved();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 flex flex-col gap-4 mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700 flex-1 text-sm">
            Réassocier <span className="font-mono">#{formId}</span>
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        {loadingEvs && (
          <div className="flex items-center gap-2 text-sm text-gray-400"><Spinner /> Chargement des essais détectés…</div>
        )}
        {error && <div className="feedback-error text-xs">{error}</div>}

        {essais && essais.length === 0 && !loadingEvs && (
          <p className="text-xs text-gray-400">
            Aucun essai détecté pour ce site — lancez d'abord « Récupérer + Associer » depuis
            Récupération des données.
          </p>
        )}

        {essais && essais.length > 0 && (
          <select
            value={selectedIdx}
            onChange={e => setSelectedIdx(e.target.value)}
            className="select select-bordered select-sm w-full text-xs"
          >
            <option value="">— choisir un essai —</option>
            {essais.map((e, i) => (
              <option key={i} value={String(i)}>
                {toParisTime(e.startTime)} → {toParisTime(e.endTime)}
                {fmtDuration(e.startTime, e.endTime) ? ` (${fmtDuration(e.startTime, e.endTime)})` : ""}
                {e.volume_l != null ? ` — ${e.volume_l} L` : ""}
              </option>
            ))}
          </select>
        )}

        {essais && essais.length > 0 && (
          <p className="text-xs text-gray-500">
            Si l'essai change, les marqueurs manuels posés dans Streamlit pour ce formulaire seront
            supprimés : ils portaient sur l'ancien essai.
          </p>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="btn btn-sm btn-ghost">Annuler</button>
          <button
            onClick={save}
            disabled={selectedIdx === "" || saving || loadingEvs}
            className="btn btn-sm btn-primary"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
